import './dashboardGaragePage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import isEmpty from 'lodash/isEmpty';
import uniqueId from 'lodash/uniqueId';
import { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ApplicationCard } from '../../components/ApplicationCard/ApplicationCard';
import { Button } from '../../components/Button/Button';
import { Header } from '../../components/Header/Header';
import { LoadingCardGroup } from '../../components/LoadingCardGroup/LoadingCardGroup';
import { NavigationPane } from '../../components/NavigationPane/NavigationPane';
import { PlaceholderCard } from '../../components/PlaceholderCard/PlaceholderCard';
import { RefandiCard } from '../../components/RefandiCard/RefandiCard';
import { resetStore } from '../../store/_core/resetStore';
import useAnalyticStore from '../../store/analyticStore/useAnalyticStore';
import useApplyStore from '../../store/applyStore/useApplyStore';
import { getVehicleApplicationDescription } from '../../store/dashboardStore/functions/getVehicleApplicationDescription';
import { getVehicleBrand } from '../../store/dashboardStore/functions/getVehicleBrand';
import { getVehicleDefaultImage } from '../../store/dashboardStore/functions/getVehicleDefaultImage';
import { getVehicleDescription } from '../../store/dashboardStore/functions/getVehicleDescription';
import { isVehicleApplicationRefandiType } from '../../store/dashboardStore/functions/isVehicleApplicationRefandiType';
import useDashboardStore from '../../store/dashboardStore/useDashboardStore';
import useThemeStore from '../../store/themeStore/useThemeStore';
import useUserStore from '../../store/userStore/useUserStore';
import { locator } from '../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { sendNewDashboardApplicationAnalytics } = useAnalyticStore();
  const {
    computed: { userFirstName },
  } = useUserStore();

  const {
    vehicles,
    fetchVehicles,
    fetchVehiclesFailedMessage,
    isFetchingVehicles,
  } = useDashboardStore();

  const newApplicationClicked = (): void => {
    resetStore(useApplyStore);

    sendNewDashboardApplicationAnalytics();

    navigate('/apply');
  };

  // The grid shows 3 cards per row. Only the first row is
  // ever filled out with placeholders, so this only applies when there are
  // 1 or 2 vehicles (0 vehicles shows the empty panel instead).
  const getPlaceholderCardCount = (): number => {
    const GRID_COLUMN_COUNT = 3;

    if (vehicles.length === 0 || vehicles.length >= GRID_COLUMN_COUNT) {
      return 0;
    }

    return GRID_COLUMN_COUNT - vehicles.length;
  };

  useEffect((): void => {
    fetchVehicles();
  }, []);

  const isEmptyState = !isFetchingVehicles && isEmpty(vehicles);

  const headClassName = ['content', 'head', isEmptyState && 'is-empty']
    .filter(Boolean)
    .join(' ');

  const placeholderCardKeys = Array.from(
    { length: getPlaceholderCardCount() },
    (): string => uniqueId('placeholder-card-'),
  );

  return (
    <div className="dashboard-garage-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <Header showDefaultLogo showDrawerToggle showLogo />

          <main data-testid={locator.pages.dashboardGarage.main.root}>
            <div className="col">
              <div className={headClassName}>
                <span>
                  <h1 data-testid={locator.pages.dashboardGarage.main.h1}>
                    {userFirstName
                      ? t('DashboardGaragePage.title.withName', {
                          firstName: userFirstName,
                        })
                      : t('DashboardGaragePage.title.default')}
                  </h1>
                  <h2 data-testid={locator.pages.dashboardGarage.main.h2}>
                    {t('DashboardGaragePage.subtitle')}
                  </h2>
                </span>
                <span>
                  <Button
                    data-testid={
                      locator.pages.dashboardGarage.main.newApplicationButton
                    }
                    variant="pill"
                    onClick={newApplicationClicked}
                  >
                    {t('DashboardGaragePage.newApplication')}
                  </Button>
                </span>
              </div>

              {!isFetchingVehicles && !isEmpty(fetchVehiclesFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={locator.pages.dashboardGarage.alert.error}
                    icon={false}
                    severity="error"
                  >
                    {fetchVehiclesFailedMessage}
                  </Alert>
                </div>
              )}

              {isFetchingVehicles && !fetchVehiclesFailedMessage && (
                <LoadingCardGroup loadingCount={3} />
              )}

              {isEmptyState && (
                <div className="empty-panel">
                  <h3 data-testid={locator.pages.dashboardGarage.main.h3}>
                    {t('DashboardGaragePage.empty.title')}
                  </h3>
                  <h4>{t('DashboardGaragePage.empty.subtitle')}</h4>
                  <Button
                    data-testid={
                      locator.pages.dashboardGarage.main
                        .emptyNewApplicationButton
                    }
                    variant="pill"
                    onClick={newApplicationClicked}
                  >
                    {t('DashboardGaragePage.newApplication')}
                  </Button>
                </div>
              )}

              {!isFetchingVehicles && !isEmpty(vehicles) && (
                <ul
                  className="content cards"
                  data-testid={locator.pages.dashboardGarage.main.cardList}
                >
                  {vehicles
                    // sort by isRefandiType to show RefandiCard first
                    .sort(
                      (a, b): number =>
                        Number(isVehicleApplicationRefandiType(b)) -
                        Number(isVehicleApplicationRefandiType(a)),
                    )
                    .map((vehicle, index): ReactNode => {
                      if (isVehicleApplicationRefandiType(vehicle)) {
                        return (
                          <RefandiCard
                            key={vehicle.id}
                            testId={locator.pages.dashboardGarage.main.getCardListItem(
                              index,
                            )}
                          />
                        );
                      }

                      return (
                        <ApplicationCard
                          applicationDescription={getVehicleApplicationDescription(
                            vehicle,
                          )}
                          brand={getVehicleBrand(vehicle)}
                          defaultImage={getVehicleDefaultImage(vehicle)}
                          description={getVehicleDescription(vehicle)}
                          id={vehicle.id}
                          key={vehicle.id}
                          testId={locator.pages.dashboardGarage.main.getCardListItem(
                            index,
                          )}
                        />
                      );
                    })}

                  {placeholderCardKeys.map(
                    (key, index): ReactNode => (
                      <PlaceholderCard
                        key={key}
                        testId={locator.pages.dashboardGarage.main.getCardListItem(
                          vehicles.length + index,
                        )}
                      />
                    ),
                  )}
                </ul>
              )}

              <footer data-testid={locator.pages.dashboardGarage.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </div>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'DashboardGaragePage';
